/**
 * Map authoring-space points on `explore_unknown_background.jpg` into the Explore Unknown
 * logical container, matching CSS `object-fit: cover` on the background bitmap.
 */

/**
 * @param {number} containerW
 * @param {number} containerH
 * @param {number} imageW — loaded bitmap natural width
 * @param {number} imageH — loaded bitmap natural height
 * @param {number} authoringW — layout authoring width (typically 1920)
 * @param {number} authoringH — layout authoring height (typically 1080)
 * @param {number} pointX — authoring-space X
 * @param {number} pointY — authoring-space Y
 * @returns {{ x: number, y: number }}
 */
export function resolveExploreUnknownAuthoringPointInCoverContainer(
  containerW,
  containerH,
  imageW,
  imageH,
  authoringW,
  authoringH,
  pointX,
  pointY
) {
  if (
    typeof containerW !== 'number' ||
    typeof containerH !== 'number' ||
    typeof imageW !== 'number' ||
    typeof imageH !== 'number' ||
    typeof authoringW !== 'number' ||
    typeof authoringH !== 'number' ||
    typeof pointX !== 'number' ||
    typeof pointY !== 'number' ||
    containerW <= 0 ||
    containerH <= 0 ||
    imageW <= 0 ||
    imageH <= 0 ||
    authoringW <= 0 ||
    authoringH <= 0
  ) {
    throw new Error('resolveExploreUnknownAuthoringPointInCoverContainer: dimensions must be positive numbers');
  }

  const imageX = (pointX / authoringW) * imageW;
  const imageY = (pointY / authoringH) * imageH;
  const scale = Math.max(containerW / imageW, containerH / imageH);
  const scaledW = imageW * scale;
  const scaledH = imageH * scale;
  const offsetX = (containerW - scaledW) / 2;
  const offsetY = (containerH - scaledH) / 2;

  return {
    x: offsetX + imageX * scale,
    y: offsetY + imageY * scale,
  };
}

/**
 * @param {number} containerW
 * @param {number} containerH
 * @param {number} imageW
 * @param {number} imageH
 * @param {number} authoringW
 * @param {number} authoringH
 * @param {{ left: number, top: number, right: number, bottom: number }} rect
 * @returns {{ x: number, y: number, width: number, height: number }}
 */
export function resolveExploreUnknownAuthoringRectInCoverContainer(
  containerW,
  containerH,
  imageW,
  imageH,
  authoringW,
  authoringH,
  rect
) {
  if (!rect || typeof rect !== 'object') {
    throw new Error('resolveExploreUnknownAuthoringRectInCoverContainer: rect is required');
  }
  const { left, top, right, bottom } = rect;
  const topLeft = resolveExploreUnknownAuthoringPointInCoverContainer(
    containerW,
    containerH,
    imageW,
    imageH,
    authoringW,
    authoringH,
    left,
    top
  );
  const bottomRight = resolveExploreUnknownAuthoringPointInCoverContainer(
    containerW,
    containerH,
    imageW,
    imageH,
    authoringW,
    authoringH,
    right,
    bottom
  );
  return {
    x: topLeft.x,
    y: topLeft.y,
    width: bottomRight.x - topLeft.x,
    height: bottomRight.y - topLeft.y,
  };
}
